import React, { useState, useEffect, useCallback } from 'react';
import NOCHeader from './components/NOCHeader';
import NOCLeftPanel from './components/NOCLeftPanel';
import NOCCenterPanel from './components/NOCCenterPanel';
import NOCRightPanel from './components/NOCRightPanel';

export default function NOC({ onBack }) {
  const [services, setServices] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [trafficHistory, setTrafficHistory] = useState([]);
  const [enhancedData, setEnhancedData] = useState(null);
  const [serverList, setServerList] = useState([]);
  const [topologyData, setTopologyData] = useState(null);
  const [workerList, setWorkerList] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [networkHistory, setNetworkHistory] = useState([]);
  const [diskHistory, setDiskHistory] = useState([]);
  const [learningStats, setLearningStats] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(10);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [containersRes, dashboardRes, alertsRes, incidentsRes, enhancedRes, serversRes, topologyRes, workersRes, securityRes, learningRes] = await Promise.all([
          fetch('/api/containers/stats'),
          fetch('/api/dashboard'),
          fetch('/api/alerts'),
          fetch('/api/incidents'),
          fetch('/api/dashboard/enhanced').catch(() => ({ ok: false })),
          fetch('/api/servers').catch(() => ({ ok: false })),
          fetch('/api/topology').catch(() => ({ ok: false })),
          fetch('/api/workers').catch(() => ({ ok: false })),
          fetch('/api/security/events?limit=10').catch(() => ({ ok: false })),
          fetch('/api/learning/stats').catch(() => ({ ok: false })),
        ]);

        if (containersRes.ok) {
          const containersData = await containersRes.json();
          const containers = containersData?.containers || [];
          setServices(containers.map(c => ({
            name: c.name,
            status: c.status === 'running' ? (c.health === 'unhealthy' ? 'warning' : 'healthy') : c.status === 'exited' ? 'exited' : 'offline',
            health: c.health,
            cpu: c.cpu_percent || 0,
            memory: c.memory_percent || 0,
            memoryUsage: c.memory_usage_mb || 0,
            memoryLimit: c.memory_limit_mb || 0,
            pids: c.pids || 0,
            uptime: c.uptime_seconds || 0,
            networkRx: c.network_rx_mb || 0,
            networkTx: c.network_tx_mb || 0,
            blockRead: c.block_read_mb || 0,
            blockWrite: c.block_write_mb || 0,
            requests: c.request_count || c.requests_per_s || 0,
            latency: c.latency_ms || c.avg_latency_ms || 0,
          })));
          setTrafficHistory(prev => [...prev.slice(-30), containers.reduce((s, c) => s + (c.request_count || c.requests_per_s || 0), 0)]);
        }

        if (dashboardRes.ok) { const d = await dashboardRes.json(); setDashboardData(d); }
        if (alertsRes.ok) { const d = await alertsRes.json(); setAlerts(d?.alerts || []); }
        if (incidentsRes.ok) { const d = await incidentsRes.json(); setIncidents(Array.isArray(d) ? d : (d.incidents || [])); }

        if (enhancedRes.ok) {
          const data = await enhancedRes.json();
          setEnhancedData(data);
          const netIO = data.network_io || {};
          const dskIO = data.disk_io || {};
          const ts = Date.now();
          setNetworkHistory(prev => {
            const now = Date.now();
            const minute = Math.floor(now / 60000) * 60000;
            const last = prev[prev.length - 1];
            if (last && last._ts === minute) {
              last.ingress = (last.ingress + (netIO.ingress_bps || 0)) / 2;
              last.egress = (last.egress + (netIO.egress_bps || 0)) / 2;
              return [...prev];
            }
            return [...prev, { _ts: minute, ingress: netIO.ingress_bps || 0, egress: netIO.egress_bps || 0 }].slice(-360);
          });
          setDiskHistory(prev => [...prev, { read: dskIO.read_bps || 0, write: dskIO.write_bps || 0 }].slice(-40));
        }

        if (serversRes.ok) { const d = await serversRes.json(); setServerList(d?.servers || []); }
        if (topologyRes.ok) { const d = await topologyRes.json(); setTopologyData(d); }
        if (workersRes.ok) { const d = await workersRes.json(); setWorkerList(d?.workers || []); }
        if (securityRes.ok) { const d = await securityRes.json(); setSecurityEvents(d?.events || []); }
        if (learningRes.ok) { const d = await learningRes.json(); setLearningStats(d); }
      } catch (e) {
        console.error('NOC fetch error:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen(); setFullscreen(true); }
    else { document.exitFullscreen(); setFullscreen(false); }
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen(); setFullscreen(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'F') {
        e.preventDefault(); toggleFullscreen();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [toggleFullscreen]);

  useEffect(() => {
    const handleFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const enhanced = enhancedData || {};
  const systemMetrics = dashboardData?.system || {};
  const networkIO = enhanced.network_io || {};
  const diskIO = enhanced.disk_io || {};
  const securityEventsSummary = enhanced.security_events || {};
  const kafkaLag = enhanced.kafka_lag || {};
  const aiStats = enhanced.ai_stats || {};
  const activityFeed = enhanced.activity_feed || [];
  const chatopsStats = enhanced.chatops_stats || {};
  const dbSizes = enhanced.database_sizes || {};

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'var(--bg)' }}>
      <NOCHeader
        currentTime={currentTime}
        fullscreen={fullscreen}
        toggleFullscreen={toggleFullscreen}
        services={services}
        incidents={incidents}
        onBack={onBack}
      />

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 animate-pulse" style={{ background: `linear-gradient(135deg, #FF2E93, #A346FF)`, filter: 'blur(16px)', borderRadius: '50%', margin: '0 auto 12px' }} />
            <p className="text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--text-3)' }}>Initializing NOC Dashboard</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 grid overflow-hidden" style={{ gridTemplateColumns: '1fr 1.2fr 1fr', gap: '6px', padding: '6px' }}>
          <NOCLeftPanel
            dashboardData={dashboardData}
            alerts={alerts}
            securityEvents={securityEvents}
            workers={workerList}
            learningStats={learningStats}
            networkIO={networkIO}
            diskIO={diskIO}
            systemMetrics={systemMetrics}
            services={services}
            serverList={serverList}
          />
          <NOCCenterPanel
            topologyData={topologyData}
            serverList={serverList}
            services={services}
            enhancedData={enhancedData}
            dashboardData={dashboardData}
            networkIO={networkIO}
            diskIO={diskIO}
            systemMetrics={systemMetrics}
            networkHistory={networkHistory}
          />
          <NOCRightPanel
            securityEvents={securityEvents}
            securityEventsSummary={securityEventsSummary}
            incidents={incidents}
            aiStats={aiStats}
            activityFeed={activityFeed}
            dbSizes={dbSizes}
            chatopsStats={chatopsStats}
          />
        </div>
      )}
    </div>
  );
}
